function humanizePivotDisplay(text) {
  const s = String(text ?? "").trim();
  if (!s) return "";
  return s.replace(/_/g, " ");
}

function formatPivotFieldValue(val) {
  if (val == null) return "";
  if (Array.isArray(val)) {
    return val
      .map((item) => String(item ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(val).trim();
}

/** Flatten review.pivot for drawer pills (console ReviewsList parity). */
export function welcomeDrawerReviewPivotEntries(pivot) {
  if (!pivot || typeof pivot !== "object" || Array.isArray(pivot)) {
    return [];
  }
  return Object.entries(pivot)
    .map(([key, rawVal]) => {
      const value = formatPivotFieldValue(rawVal);
      if (!value) return null;
      const keyStr = String(key ?? "").trim();
      const valueH = humanizePivotDisplay(value);
      const label =
        !keyStr || keyStr === "vendorName"
          ? valueH
          : `${humanizePivotDisplay(keyStr)}: ${valueH}`;
      return { key: keyStr || label, value, label };
    })
    .filter(Boolean);
}

export function welcomeDrawerReviewPivotLabels(pivot) {
  return welcomeDrawerReviewPivotEntries(pivot).map((entry) => entry.label);
}

/**
 * Normalize welcome / highlights review rows for WelcomeDrawerSimpleReviewCard.
 * API and docs_of_dashboard may use different field names.
 */
export function normalizeWelcomeDrawerReviewDoc(raw) {
  if (raw == null || typeof raw !== "object") {
    return null;
  }
  const text = String(
    raw.text ??
      raw.review_text ??
      raw.ReviewText ??
      raw.reviewText ??
      raw.DocText ??
      raw.body ??
      "",
  ).trim();
  const title = String(
    raw.title ??
      raw.review_title ??
      raw.ReviewTitle ??
      raw.question ??
      "",
  ).trim();
  const displayText = text || title;
  if (!displayText) {
    return null;
  }
  return {
    ...raw,
    text: displayText,
    title,
    timestamp:
      raw.timestamp ??
      raw.date ??
      raw.review_date ??
      raw.num_timestamp ??
      null,
    date_gpid:
      raw.date_gpid ?? raw.gpid ?? raw.review_id ?? raw.internalId ?? null,
    num_rating:
      raw.num_rating != null && raw.num_rating !== ""
        ? raw.num_rating
        : raw.rating,
    pivot:
      raw.pivot && typeof raw.pivot === "object"
        ? raw.pivot
        : raw.restaurant && typeof raw.restaurant === "object"
          ? raw.restaurant
          : null,
  };
}

/** Parse `{ success: [...] }` and common variants from apiRequest. */
export function extractWelcomeReviewsFromApiResponse(resp) {
  if (!resp || resp === false || resp.cancel) {
    return [];
  }
  if (resp.error != null && resp.success == null) {
    return [];
  }
  const s = resp.success;
  let rows = [];
  if (Array.isArray(s)) {
    rows = s;
  } else if (s && typeof s === "object") {
    if (Array.isArray(s.reviews)) {
      rows = s.reviews;
    } else if (Array.isArray(s.items)) {
      rows = s.items;
    } else if (Array.isArray(s.data)) {
      rows = s.data;
    }
  }
  return sortReviewsNewestFirst(
    rows.map((row) => normalizeWelcomeDrawerReviewDoc(row)).filter(Boolean),
  );
}

function reviewTimestampMs(row) {
  const raw = row?.timestamp ?? row?.date ?? row?.num_timestamp;
  if (raw == null || raw === "") return 0;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw < 1e12 ? raw * 1000 : raw;
  }
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? 0 : ms;
}

/** Newest review first. Stable for already-sorted API pages. */
export function sortReviewsNewestFirst(reviews) {
  if (!Array.isArray(reviews) || reviews.length < 2) {
    return Array.isArray(reviews) ? reviews : [];
  }
  return [...reviews].sort((a, b) => reviewTimestampMs(b) - reviewTimestampMs(a));
}
